import { z } from "zod"

import type { ProductSelectionItem, PurchaseOrderFormData } from "../Supplier/types/types"

import type { AerosDateValue } from "@/components/UiKit/AerosDatePicker/AerosDatePickerWithCustomActions"
import type { Comment, Company, Order, PricingPackage, Product } from "@/graphql/codegen/graphql"
import type { LocationAssociation } from "@/screens/Products/components/ProductShipLocationSelect"

export type ShipToLocationWithWarehouse = LocationAssociation & {
  warehouseAreaId: string
  warehouseExternalSystemId: string
}

export enum OrderDocumentType {
  QUOTE = "quote",
  PURCHASE_ORDER = "purchaseOrder",
}

export interface PurchaseOrderDialogProps {
  isSubmitting: boolean
  company: Company
  products: ProductSelectionItem[]
  onClose: () => void
  onSubmit: (
    data: PurchaseOrderFormData,
    selectedProducts: ProductSelectionItem[],
    requestedDeliveryDate: string | null
  ) => void
}

export type PurchaseOrderShipToFormValue = Pick<
  ShipToLocationWithWarehouse,
  "locationAssociationId" | "warehouseAreaId"
>

export const PurchaseOrderDialogSchema = z.object({
  shipTo: z.string().min(1, "Please select a ship to location"),
})

export type PurchaseOrderDialogFormValues = z.infer<typeof PurchaseOrderDialogSchema>

export type PurchaseOrderIds = Pick<Order, "externalSystemId" | "orderId">

export interface ClassifiedPricingPackages {
  currentPricing: PricingPackage | undefined
  futurePricing: PricingPackage[]
}

export interface PurchaseOrderForm
  extends Omit<Order, "orderLines" | "requestedDeliveryDate" | "confirmedShipmentDate"> {
  // Override fields that getPurchaseOrderForm transforms
  requestedDeliveryDate: AerosDateValue
  confirmedShipmentDate: AerosDateValue
  orderLines: {
    numberOfPallets: number
    pricePerUom: number
    quantityInUom: number
    productId: number
    productVersionId: number
    uomId: string
    priceUomId: string
    isManualPriceSet: boolean
    product: Product
  }[]
  currentComment: Record<string, unknown>
  comments: Comment[]
}

export interface ChangePricingUoMFormData {
  priceUomId: string
  pricePerUom: number
  isManualPriceSet: boolean
}

export interface OrderLineFormData {
  numberOfPallets: number
  pricePerUom: number
  quantityInUom: number
  productId: number
  productVersionId: number
  uomId: string
  priceUomId: string
  isManualPriceSet: boolean
  product: Product
}

export interface OrderLinePriceFormData {
  numberOfPallets: number
  pricePerUom: number
  quantityInUom: number
  productId: number
  productVersionId: number
  uomId: string
  priceUomId: string
  isManualPriceSet: boolean
  product: Product
}

export interface ChangePricingUoMDialogResult {
  updatedOrderLines: OrderLinePriceFormData[]
  orderLine: OrderLineFormData
}
